import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Wraps React.lazy with automatic retry + full page reload on chunk load failure.
 * Handles the scenario where a deploy changes chunk hashes and stale clients
 * try to load old chunks that no longer exist (404).
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 1
): LazyExoticComponent<T> {
  return lazy(() => retryImport(factory, retries));
}

async function retryImport<T>(
  factory: () => Promise<T>,
  retries: number
): Promise<T> {
  try {
    const result = await factory();
    // Chunk loaded successfully — clear any stale reload flag
    sessionStorage.removeItem('chunk-reload');
    return result;
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return retryImport(factory, retries - 1);
    }

    // All retries exhausted — likely a deploy changed chunk hashes.
    // Reload the page once to get fresh HTML with new chunk URLs.
    const hasReloaded = sessionStorage.getItem('chunk-reload');
    if (!hasReloaded) {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
      return new Promise(() => {});
    }

    sessionStorage.removeItem('chunk-reload');
    throw error;
  }
}
